/**
 * =========================================================================================
 *     Copyright © 2025 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   DeltaCompare.tsx
 *     Description     :   Delta Compare page — compare backup data against current
 *                         lakehouse state to detect changes.
 *     Author          :   Peter Dobler
 *     Modified        :   Conformed to Spec 197 design blueprint — migrated the bespoke
 *                         header / cards / stat tiles / search input onto the canonical
 *                         Tailwind+token UI kit. Presentation-only; data, hooks, and the
 *                         CompareWizard / CompareResultsTable / PastComparisons children
 *                         are unchanged.
 * =========================================================================================
 */

import React, { useState } from 'react';
import { GitCompareArrows, RefreshCw, Search } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useCompareBackup, DeltaCompareResult } from '../hooks/useDeltaCompare';
import { CompareWizard } from '../components/compare/CompareWizard';
import { CompareResultsTable } from '../components/compare/CompareResultsTable';
import { PastComparisons } from '../components/compare/PastComparisons';
import { useLakehouseCatalog } from '../hooks/useLakehouses';
import {
  PageLayout,
  Card,
  Button,
  Input,
  StatCard,
  EmptyState,
  ErrorState,
  LoadingState,
  AppTooltip,
  PageHelpPanel,
} from '../../../components/ui';
import { backupOperationsHelp } from '../../../data/help/backupOperationsHelp';
import { formatEastern } from '@/utils/dateFormatter';

export const DeltaCompare: React.FC = () => {
  const [selectedLakehouseId, setSelectedLakehouseId] = useState<string>('');
  const [lakehouseQuery, setLakehouseQuery] = useState('');
  const [lakehouseOpen, setLakehouseOpen] = useState(false);
  const [compareResult, setCompareResult] = useState<DeltaCompareResult | null>(null);

  // Delta Compare only applies to backup-enabled lakehouses (the only ones with a
  // backup to compare current state against). Filter server-side via is_backup_enabled
  // — the catalog response's per-row `enabled` flag is unreliable, so we let SQL filter.
  const { data: lakehousesData, isLoading: isLoadingLakehouses } = useLakehouseCatalog(
    { is_backup_enabled: true },
    { page: 1, pageSize: 1000 }
  );

  const compareBackupMutation = useCompareBackup();

  const backupEnabledLakehouses = lakehousesData?.data ?? [];
  const selectedLakehouse = backupEnabledLakehouses.find((lh) => lh.id === selectedLakehouseId);

  const lakehouseQueryNormalized = lakehouseQuery.trim().toLowerCase();
  const filteredLakehouses = lakehouseQueryNormalized
    ? backupEnabledLakehouses.filter((lh) =>
        `${lh.lakehouseName} ${lh.workspaceDisplayName ?? ''}`
          .toLowerCase()
          .includes(lakehouseQueryNormalized)
      )
    : backupEnabledLakehouses;

  const handleCompare = async (backupRunId: string, options: { tableFilter?: string[] }) => {
    if (!selectedLakehouseId) return;

    try {
      const result = await compareBackupMutation.mutateAsync({
        lakehouseId: selectedLakehouseId,
        backupRunId,
        tableFilter: options.tableFilter,
      });
      setCompareResult(result);
    } catch {
      // Failure is surfaced via the error banner below (compareBackupMutation.isError).
    }
  };

  const handleReset = () => {
    setCompareResult(null);
  };

  // Spec 298 FR-006: a total that was not counted (no current snapshot) reads "—", never 0
  const formatNumber = (num: number | null): string => (num === null ? '—' : new Intl.NumberFormat().format(num));

  return (
    <PageLayout
      title="Delta Compare"
      description="Compare backup data against current lakehouse state to detect changes"
      icon={<GitCompareArrows className="h-6 w-6" />}
      actions={<PageHelpPanel content={backupOperationsHelp} buttonSize="sm" />}
    >
      <div className="space-y-6">
        {/* Lakehouse Selection */}
        {!compareResult && (
          <Card>
            <h3 className="text-lg font-medium text-[var(--color-text-primary)]">Select Lakehouse</h3>
            <p className="mb-4 text-xs text-[var(--color-text-secondary)]">
              Only backup-enabled lakehouses are shown — they&apos;re the ones with backups to compare against.
            </p>
            {isLoadingLakehouses ? (
              <LoadingState rows={3} />
            ) : backupEnabledLakehouses.length > 0 ? (
              <AppTooltip title="Search and select the lakehouse to compare backup data against its current state">
                <div className="relative">
                  <Input
                    isFullWidth
                    type="text"
                    leftAddon={<Search className="h-4 w-4" />}
                    value={
                      lakehouseOpen
                        ? lakehouseQuery
                        : selectedLakehouse
                          ? `${selectedLakehouse.lakehouseName} (${selectedLakehouse.workspaceDisplayName})`
                          : ''
                    }
                    onChange={(e) => {
                      setLakehouseQuery(e.target.value);
                      setLakehouseOpen(true);
                    }}
                    onFocus={() => {
                      setLakehouseQuery('');
                      setLakehouseOpen(true);
                    }}
                    onBlur={() => setTimeout(() => setLakehouseOpen(false), 150)}
                    placeholder="Search lakehouses by name or workspace…"
                  />
                  {lakehouseOpen && (
                    <ul className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] shadow-lg">
                      {filteredLakehouses.length === 0 && (
                        <li className="px-3 py-2 text-sm text-[var(--color-text-secondary)]">
                          No matching lakehouses
                        </li>
                      )}
                      {filteredLakehouses.slice(0, 100).map((lakehouse) => (
                        <li key={lakehouse.id}>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedLakehouseId(lakehouse.id);
                              setLakehouseOpen(false);
                              setLakehouseQuery('');
                            }}
                            className={`w-full px-3 py-2 text-left text-sm text-[var(--color-text-primary)] hover:bg-[var(--color-bg-subtle)] ${
                              lakehouse.id === selectedLakehouseId ? 'bg-[var(--color-bg-subtle)]' : ''
                            }`}
                          >
                            {lakehouse.lakehouseName}
                            <span className="text-[var(--color-text-secondary)]">
                              {' '}
                              ({lakehouse.workspaceDisplayName})
                            </span>
                          </button>
                        </li>
                      ))}
                      {filteredLakehouses.length > 100 && (
                        <li className="px-3 py-2 text-xs text-[var(--color-text-secondary)]">
                          Showing first 100 of {filteredLakehouses.length} — refine your search…
                        </li>
                      )}
                    </ul>
                  )}
                </div>
              </AppTooltip>
            ) : (
              <EmptyState
                title="No backup-enabled lakehouses available"
                message="Enable backups on a lakehouse to compare its backup against current state."
              />
            )}
          </Card>
        )}

        {/* Compare Wizard */}
        {selectedLakehouseId && !compareResult && selectedLakehouse && (
          <CompareWizard
            lakehouseId={selectedLakehouseId}
            lakehouseName={selectedLakehouse.lakehouseName}
            onCompare={handleCompare}
            isComparing={compareBackupMutation.isPending}
          />
        )}

        {/* Comparison error (surfaced — not swallowed) */}
        {!compareResult && compareBackupMutation.isError && (
          <ErrorState
            title="Comparison failed"
            message={(compareBackupMutation.error as Error)?.message ?? 'Unknown error'}
          />
        )}

        {/* Past comparisons (re-open a persisted run) */}
        {selectedLakehouseId && !compareResult && (
          <PastComparisons lakehouseId={selectedLakehouseId} onOpen={setCompareResult} />
        )}

        {/* Results */}
        {compareResult && (
          <>
            {compareResult.currentSnapshotMissing && (
              <Card className="border-[var(--color-status-warning)]/40 bg-[var(--color-status-warning)]/5">
                <p className="text-sm text-[var(--color-text-secondary)]">
                  No current table snapshot is available for this lakehouse, so only the backup
                  side is shown. Run collection for this lakehouse, then compare again.
                </p>
              </Card>
            )}

            {/* Summary Card */}
            <Card>
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-lg font-medium text-[var(--color-text-primary)]">Comparison Results</h3>
                <AppTooltip title="Reset results and start a new delta comparison">
                  <Button
                    variant="outline"
                    size="sm"
                    leftIcon={<RefreshCw className="h-4 w-4" />}
                    onClick={handleReset}
                  >
                    New Comparison
                  </Button>
                </AppTooltip>
              </div>

              {/* Lakehouse Info */}
              <div className="mb-4 rounded-md bg-[var(--color-bg-subtle)] p-4">
                <h4 className="mb-2 text-sm font-medium text-[var(--color-text-primary)]">Comparison Details</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-[var(--color-text-secondary)]">Lakehouse:</span>
                    <p className="font-medium text-[var(--color-text-primary)]">{compareResult.lakehouse_name}</p>
                  </div>
                  <div>
                    <span className="text-[var(--color-text-secondary)]">Backup Date:</span>
                    <p className="font-medium text-[var(--color-text-primary)]">
                      {formatDistanceToNow(new Date(compareResult.backup_timestamp), {
                        addSuffix: true,
                      })}
                    </p>
                  </div>
                  <div>
                    <span className="text-[var(--color-text-secondary)]">Comparison Date:</span>
                    <p className="font-medium text-[var(--color-text-primary)]">
                      {formatEastern(compareResult.comparison_timestamp)}
                    </p>
                  </div>
                  <div>
                    <span className="text-[var(--color-text-secondary)]">Tables Compared:</span>
                    <p className="font-medium text-[var(--color-text-primary)]">{compareResult.tables_compared}</p>
                  </div>
                </div>
              </div>

              {/* Summary Statistics */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
                <StatCard label="Tables Compared" value={compareResult.tables_compared} />
                <StatCard
                  label="With Changes"
                  value={compareResult.tables_with_changes}
                  valueClassName="text-[var(--color-status-warning)]"
                />
                <StatCard
                  label="Tables Added"
                  value={compareResult.summary.tables_added}
                  valueClassName="text-[var(--color-status-success)]"
                />
                <StatCard
                  label="Tables Removed"
                  value={compareResult.summary.tables_removed}
                  valueClassName="text-[var(--color-status-danger)]"
                />
                <StatCard
                  label="Row Difference"
                  value={formatNumber(compareResult.total_row_diff)}
                  valueClassName="text-[var(--color-accent)]"
                />
              </div>

              {/* Row Count Summary */}
              <div className="mt-4 grid grid-cols-2 gap-4">
                <StatCard
                  label="Total Backup Rows"
                  value={formatNumber(compareResult.summary.total_backup_rows)}
                />
                <StatCard
                  label="Total Current Rows"
                  value={formatNumber(compareResult.summary.total_current_rows)}
                />
              </div>
            </Card>

            {/* Results Table */}
            <Card padding="none">
              <div className="border-b border-[var(--color-border)] px-6 py-4">
                <h3 className="text-lg font-medium text-[var(--color-text-primary)]">Table-by-Table Comparison</h3>
              </div>
              <CompareResultsTable tables={compareResult.tables} />
            </Card>
          </>
        )}
      </div>
    </PageLayout>
  );
};
